import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

/* ═══════════════════════════════════════════════════
   RECOMMENDED - pure Tailwind, sleek hover language.
   Mirrors the Categories card hover treatment.
   ═══════════════════════════════════════════════════ */

const RESPONSIVE = {
  xl: { breakpoint: { max: 4000, min: 1280 }, items: 5 },
  lg: { breakpoint: { max: 1280, min: 960  }, items: 4 },
  md: { breakpoint: { max: 960,  min: 600  }, items: 3 },
  xs: { breakpoint: { max: 600,  min: 0    }, items: 1.4 },
};

const trim = (s, n) => (s && s.length > n ? s.substring(0, n) + '…' : s || '');

// Custom dot styling - fully overrides react-multi-carousel defaults via inline <style> below.

export const Recommended = ({ recs }) => {
  if (!recs || recs.length === 0) return null;

  return (
    <section className="py-8 md:py-12">
      {/* Section header */}
      <div className="mb-7 md:mb-9">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-amber">Recommended</div>
        <h2 className="text-2xl md:text-[28px] font-bold leading-[1.15] tracking-[-0.025em] text-fg-primary">
          Recommended for you
        </h2>
        <p className="mt-2 max-w-[560px] text-[14px] leading-relaxed text-fg-secondary">
          Top-rated listings from trusted providers near you.
        </p>
      </div>

      {/* Carousel */}
      <div className="esay-recommended-carousel">
        <Carousel
          responsive={RESPONSIVE}
          autoPlay
          infinite
          autoPlaySpeed={4000}
          swipeable
          showDots
          arrows={false}
          transitionDuration={500}
          itemClass="px-2"
        >
          {recs.map((item) => (
            <RouterLink
              key={item.product_id}
              to={`/psdetails/${item.product_slug}/${item.product_id}`}
              title={item.product_name}
              className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border-subtle bg-surface-paper transition-[transform,border-color,box-shadow] duration-300 ease-out-soft hover:-translate-y-1 hover:border-amber hover:shadow-amber-glow-strong"
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={item.product_image}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out-soft group-hover:scale-[1.07]"
                />
                {/* Subtle bottom fade for image edge */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/40 to-transparent transition-opacity duration-500 ease-out-soft group-hover:opacity-60"
                />
                {/* Amber sweep flares in on hover */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-out-soft group-hover:opacity-100"
                  style={{
                    background:
                      'radial-gradient(ellipse 80% 50% at 50% 100%, rgba(251,191,36,0.16), transparent 70%)',
                  }}
                />
              </div>

              {/* Content */}
              <div className="flex flex-1 flex-col gap-1.5 p-4">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber transition-colors duration-200 ease-out-soft group-hover:text-amber-light">
                  {trim(item.sub_category_name, 30)}
                </span>

                <h3 className="text-[15px] font-semibold leading-tight tracking-[-0.01em] text-fg-primary line-clamp-2 transition-transform duration-300 ease-out-soft group-hover:-translate-y-0.5">
                  {trim(item.product_name, 30)}
                </h3>

                <div className="mt-auto flex items-center gap-1.5 pt-1.5 text-[12px] text-fg-tertiary">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    className="h-3 w-3 shrink-0"
                  >
                    <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
                    <circle cx="12" cy="9" r="3" />
                  </svg>
                  <span className="truncate">{trim(item.product_location_text, 30)}</span>
                </div>

                {/* Animated amber underline below content */}
                <div className="mt-2 flex items-center gap-2">
                  <span
                    aria-hidden
                    className="block h-[2px] w-5 bg-amber transition-[width,background-color] duration-500 ease-out-soft group-hover:w-10 group-hover:bg-amber-light"
                  />
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber opacity-0 -translate-x-1 transition-[opacity,transform] duration-300 ease-out-soft group-hover:opacity-100 group-hover:translate-x-0">
                    View
                  </span>
                </div>
              </div>
            </RouterLink>
          ))}
        </Carousel>
      </div>

      <style>{`
        .esay-recommended-carousel .react-multi-carousel-list {
          padding-bottom: 36px;
          overflow: visible;
        }
        .esay-recommended-carousel .react-multi-carousel-dot-list {
          padding: 4px 0;
          bottom: 4px;
          gap: 8px;
        }
        .esay-recommended-carousel .react-multi-carousel-dot {
          margin: 0;
          padding: 0;
        }
        .esay-recommended-carousel .react-multi-carousel-dot button {
          background: rgba(251, 191, 36, 0.32);
          border: 0;
          width: 8px;
          height: 8px;
          margin: 0 4px;
          padding: 0;
          border-radius: 9999px;
          opacity: 1;
          box-shadow: 0 0 0 1px rgba(251,191,36,0.18);
          transition: all 280ms cubic-bezier(0.16, 1, 0.3, 1);
          cursor: pointer;
        }
        .esay-recommended-carousel .react-multi-carousel-dot button:hover {
          background: rgba(251, 191, 36, 0.6);
          transform: scale(1.15);
        }
        .esay-recommended-carousel .react-multi-carousel-dot--active button {
          background: #FBBF24;
          width: 28px;
          box-shadow: 0 0 0 1px rgba(251,191,36,0.4), 0 0 12px rgba(251,191,36,0.6);
        }
      `}</style>
    </section>
  );
};
